/**
 * SitesAtGlance — home-dashboard "Sites at a glance" row.
 *
 * Owner: drafts/55 v0.9 — multi-site UX.
 *
 * Renders one mini-card per registered site with the four highest-signal
 * KPIs (open CAPAs / critical+major deviations / training overdue / days
 * to license expiry). Clicking a card sets the global site scope so every
 * list page in the SPA filters to that site.
 */

import { Link } from '@tanstack/react-router';
import { useSiteKpis, type SiteKpis } from '../../api/orgHooks';
import { useSiteScope, type SiteScope } from '../../auth/SiteScopeContext';
import styles from './SitesAtGlance.module.css';

const KIND_ICON: Record<SiteKpis['site_kind'], string> = {
  MANUFACTURING_HQ: '🏭',
  MANUFACTURING: '🏭',
  PACKAGING: '📦',
  WAREHOUSE: '🏬',
  LAB_QC: '🧪',
  HEAD_OFFICE: '🏢',
  DISTRIBUTION: '🚚',
};

function bandForCapa(open: number, overdue: number): 'green' | 'amber' | 'red' {
  if (overdue >= 2) return 'red';
  if (overdue >= 1 || open >= 8) return 'amber';
  return 'green';
}
function bandForDeviation(criticalMajor: number): 'green' | 'amber' | 'red' {
  if (criticalMajor >= 6) return 'red';
  if (criticalMajor >= 3) return 'amber';
  return 'green';
}
function bandForTraining(overduePct: number): 'green' | 'amber' | 'red' {
  if (overduePct >= 8) return 'red';
  if (overduePct >= 3) return 'amber';
  return 'green';
}
function bandForLicense(days: number | null): 'green' | 'amber' | 'red' {
  if (days == null) return 'amber';
  if (days < 90) return 'red';
  if (days < 180) return 'amber';
  return 'green';
}

export function SitesAtGlance() {
  const q = useSiteKpis();
  const { scope, setScope } = useSiteScope();
  const sites = q.data?.items ?? [];

  if (!q.isLoading && sites.length === 0) return null;

  return (
    <section className={styles.section} aria-label="Sites at a glance">
      <header className={styles.head}>
        <h2 className={styles.title}>Sites at a glance</h2>
        <Link to="/admin/sites" className={styles.viewAll}>
          View site registry →
        </Link>
      </header>

      <div className={styles.grid}>
        {q.isLoading && sites.length === 0 && (
          <>
            <div className={styles.skeleton} />
            <div className={styles.skeleton} />
            <div className={styles.skeleton} />
          </>
        )}
        {sites.map((s) => {
          const active = scope.kind === 'site' && scope.siteId === s.site_id;
          const days = s.days_to_license_expiry;
          return (
            <button
              key={s.site_id}
              type="button"
              className={`${styles.card} ${active ? styles.cardActive : ''}`}
              onClick={() => {
                if (active) {
                  setScope({ kind: 'all' } satisfies SiteScope);
                } else {
                  setScope({
                    kind: 'site',
                    siteId: s.site_id,
                    siteCode: s.site_code,
                    siteName: s.site_name,
                  } satisfies SiteScope);
                }
              }}
              aria-pressed={active}
              title={
                active
                  ? `${s.site_code} is the active scope — click again to clear`
                  : `Switch scope to ${s.site_code}`
              }
            >
              <div className={styles.cardHead}>
                <span className={styles.cardIcon} aria-hidden>
                  {KIND_ICON[s.site_kind] ?? '🏭'}
                </span>
                <div className={styles.cardTitleBlock}>
                  <span className={styles.cardCode}>{s.site_code}</span>
                  <span className={styles.cardCity}>
                    {s.city ?? ''}
                    {s.city && ' · '}
                    {s.site_kind.replaceAll('_', ' ').toLowerCase()}
                  </span>
                </div>
                {active && (
                  <span className={styles.activeBadge} aria-hidden>
                    Active
                  </span>
                )}
              </div>

              <div className={styles.kpiRow}>
                <Kpi
                  label="CAPA open"
                  value={s.kpis.capa_open}
                  band={bandForCapa(s.kpis.capa_open, s.kpis.capa_overdue)}
                  sub={s.kpis.capa_overdue > 0 ? `${s.kpis.capa_overdue} overdue` : 'on track'}
                />
                <Kpi
                  label="Dev. CRT/MAJ"
                  value={s.kpis.deviation_critical_major}
                  band={bandForDeviation(s.kpis.deviation_critical_major)}
                  sub={s.kpis.oos_open > 0 ? `${s.kpis.oos_open} OOS open` : 'no OOS'}
                />
                <Kpi
                  label="Train. overdue"
                  value={`${s.kpis.training_overdue_pct}%`}
                  band={bandForTraining(s.kpis.training_overdue_pct)}
                  sub={`${s.kpis.training_overdue} late`}
                />
                <Kpi
                  label="License"
                  value={days == null ? '—' : days < 0 ? 'EXPIRED' : `${days}d`}
                  band={bandForLicense(days)}
                  sub={s.license_ref ?? ''}
                />
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function Kpi({
  label,
  value,
  band,
  sub,
}: {
  label: string;
  value: number | string;
  band: 'green' | 'amber' | 'red';
  sub?: string;
}) {
  return (
    <div className={`${styles.kpi} ${styles[`kpi_${band}`]}`}>
      <span className={styles.kpiLabel}>{label}</span>
      <span className={styles.kpiValue}>{value}</span>
      {sub && <span className={styles.kpiSub}>{sub}</span>}
    </div>
  );
}
